'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import {
  Factory,
  Flame,
  Ruler,
  Volume2,
} from 'lucide-react'
import SafeImage from './SafeImage'

function CountUp({
  value,
  suffix = '',
  prefix = '',
}: {
  value: number
  suffix?: string
  prefix?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    let started = false

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started) return
        started = true
        const start = performance.now()
        const duration = 1800

        const tick = (now: number) => {
          const progress = Math.min((now - start) / duration, 1)
          setCurrent(Math.floor(progress * value))
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
        observer.disconnect()
      },
      { threshold: 0.3 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [value])

  return (
    <div ref={ref} className="text-2xl md:text-3xl font-bold text-[#111827] tracking-tight">
      {prefix}
      {current}
      {suffix}
    </div>
  )
}

const stats = [
  { value: 11, suffix: '+', label: 'Yıllık üretim tecrübesi' },
  { value: 2895, suffix: '+', label: 'Teslim edilmiş panel projesi' },
  { value: 100, prefix: '%', suffix: '', label: 'Yerli üretim' },
]

const reasons = [
  {
    icon: Factory,
    title: 'Yerli Üretim',
    description:
      'Tasarım, kesim, kaplama ve kalite kontrol Türkiye’deki tesislerimizde yürütülür. Özel ölçü talepler hızlı ve kontrollü şekilde karşılanır.',
  },
  {
    icon: Flame,
    title: 'A2-s1, d0 Yangın Sınıfı',
    description:
      'Yangına dayanımlı malzeme yapısıyla A2-s1, d0 sınıfına uygun paneller üretilir. Ofis ve kamu projelerinde güvenlik önceliği korunur.',
  },
  {
    icon: Volume2,
    title: 'NRC 0.95-1.00 Değeri',
    description:
      'Yüksek NRC değeriyle yankıyı güçlü biçimde emer. Konuşma anlaşılırlığını artırır ve mekân içi gürültü seviyesini dengeler.',
  },
  {
    icon: Ruler,
    title: 'Estetik ve Dayanıklı Kaplama',
    description:
      'Kumaş, ahşap ve keçe seçenekleriyle mekânın mimarisine uyum sağlayan, uzun ömürlü yüzeyler sunulur.',
  },
]

export default function AboutSection() {
  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="relative">
            <div className="relative overflow-hidden rounded-[28px] md:rounded-[36px] bg-[#0b1220] shadow-xl aspect-[4/5] max-h-[560px] lg:max-h-none lg:aspect-[4/5]">
              <SafeImage
                src="/images/akustik-panel.webp"
                alt="Akustik panel - Doku Akustik üretimi"
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-cover"
                priority={false}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-[2px] bg-[#4b5563]" />
              <span className="text-[11px] md:text-xs font-semibold tracking-[0.22em] uppercase text-[#4b5563]">
                BİZ KİMİZ?
              </span>
            </div>

            <h2 className="text-2xl md:text-[2rem] lg:text-[2.15rem] font-bold leading-tight mb-4">
              <span className="text-[#b45309]">Akustik Panel Üreticisi</span>{' '}
              <span
                className="font-extrabold"
                style={{
                  background: 'linear-gradient(to top right, #014b64, #01124d)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Doku Akustiği Yakından Tanıyın
              </span>
            </h2>

            <div className="space-y-3 text-[14px] md:text-[15px] text-[#4b5563] leading-relaxed mb-6">
              <p>
                2015 yılından bu yana ofis, stüdyo ve ticari alanlar için{' '}
                <Link href="/akustik-panel/" className="text-[#b45309] font-medium hover:underline">
                  akustik panel
                </Link>{' '}
                üreten Doku Akustik, yankısız, dengeli ve konforlu mekânlar kurmak üzere
                yüzlerce projeye imza atmıştır.{' '}
                <Link
                  href="/urun/kumas-kapli-akustik-panel/"
                  className="text-[#b45309] font-medium hover:underline"
                >
                  Kumaş kaplı panellerden
                </Link>{' '}
                ahşap ve keçe panellere, baffle sistemlerinden ofis paravanlarına kadar
                geniş bir ürün gamını kendi tesislerinde üretir.
              </p>
              <p>
                Türk üreticilerden oluşan ekibiyle tasarımdan montaja kadar tüm süreçleri
                kendi bünyesinde yürütür. Her panel ses emilim performansı, malzeme
                dayanımı, mekân estetiği ve uzun ömürlü kullanım hedefiyle Türkiye’de
                özenle üretilir.
              </p>
              <p>
                Doku Akustik’in ürettiği paneller CE, ISO ve TSE belgelendirme
                süreçleriyle uyumlu şekilde hazırlanır. Talep edildiğinde sertifikalar
                müşteriye şeffaf biçimde sunulur.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 md:gap-4 py-5 mb-6 border-y border-gray-200">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`text-center md:text-left px-1 md:px-3 ${
                    index > 0 ? 'border-l border-gray-200' : ''
                  }`}
                >
                  <CountUp
                    value={stat.value}
                    suffix={stat.suffix}
                    prefix={stat.prefix || ''}
                  />
                  <div className="text-[10px] md:text-xs text-[#4b5563] mt-1 leading-snug">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {reasons.map((reason) => {
                const Icon = reason.icon
                return (
                  <div key={reason.title} className="flex gap-3.5">
                    <div className="w-10 h-10 rounded-full bg-gray-100 text-[#374151] flex items-center justify-center shrink-0">
                      <Icon size={18} />
                    </div>
                    <div>
                      <div className="font-bold text-sm text-[#111827] mb-0.5">
                        {reason.title}
                      </div>
                      <p className="text-xs md:text-[13px] text-[#4b5563] leading-relaxed">
                        {reason.description}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
